import { useState, type FormEvent } from "react";
import { Eye, EyeOff, Mail } from "lucide-react";
import { AuthLayout } from "../components/AuthLayout";
import { Button } from "../components/Button";
import { FieldInput } from "../components/Field";
import { MessageBanner } from "../components/MessageBanner";
import { useSession } from "../lib/session/SessionContext";

interface SignupScreenProps {
  readonly invitedEmail: string;
  readonly onUseExistingAccount: () => void;
}

const MIN_PASSWORD_LENGTH = 6;

export function SignupScreen({ invitedEmail, onUseExistingAccount }: SignupScreenProps) {
  const { signup, error, clearError } = useSession();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setLocalError(`A senha precisa de ao menos ${MIN_PASSWORD_LENGTH} caracteres.`);
      return;
    }
    if (password !== confirm) {
      setLocalError("As senhas não coincidem.");
      return;
    }
    setBusy(true);
    try {
      await signup(invitedEmail, password);
    } finally {
      setBusy(false);
    }
  };

  const message = localError ?? error;

  return (
    <AuthLayout
      title="Crie seu acesso"
      subtitle="Você foi convidado para um CT Apogeu. Defina uma senha para entrar."
      footer={<span>Apogeu App · alunos e professores</span>}
    >
      <form className="auth-form" onSubmit={submit}>
        <FieldInput
          label="E-mail do convite"
          type="email"
          value={invitedEmail}
          readOnly
          disabled
          trailing={<Mail size={18} className="field__icon" aria-hidden="true" />}
        />
        <FieldInput
          label="Senha"
          type={showPassword ? "text" : "password"}
          autoComplete="new-password"
          placeholder="Crie uma senha"
          value={password}
          onChange={e => setPassword(e.target.value)}
          trailing={
            <button
              type="button"
              className="field__action"
              onClick={() => setShowPassword(value => !value)}
              aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          }
        />
        <FieldInput
          label="Confirme a senha"
          type={showPassword ? "text" : "password"}
          autoComplete="new-password"
          placeholder="Repita a senha"
          value={confirm}
          onChange={e => setConfirm(e.target.value)}
        />
        {message && <MessageBanner>{message}</MessageBanner>}
        <Button type="submit" fullWidth disabled={busy}>
          {busy ? "Criando acesso..." : "Criar conta e continuar"}
        </Button>
        <button
          type="button"
          className="link-button link-button--center"
          onClick={() => {
            clearError();
            onUseExistingAccount();
          }}
        >
          Já tenho conta
        </button>
      </form>
    </AuthLayout>
  );
}
